import React, { useState, useEffect, useCallback } from 'react';
import { useQuotes } from '../../context/QuoteContext';
import PaperSizeSelector from '../../components/small-format/PaperSizeSelector';
import QuoteReview from './QuoteReview';
import ManagerQuoteReview from '../../components/manager/ManagerQuoteReview';
import PrintlogicSuccessModal from '../../components/common/PrintlogicSuccessModal';
import CustomPricingBanner from '../../components/common/CustomPricingBanner';
import ImpositionVisualizer from '../../components/small-format/ImpositionVisualizer';
import BookletOptionsForm from '../../components/small-format/BookletOptionsForm';
import PaperUsagePreview from '../../components/small-format/PaperUsagePreview';
import PriceBreakdownTable from '../../components/small-format/PriceBreakdownTable';
import { useSmallFormatData } from '../../hooks/useSmallFormatData';
import PaperSelector from '../../components/small-format/PaperSelector';
import { useAuth } from '../../context/AuthContext';
import FinishingMaterialOptions from '../../components/small-format/FinishingMaterialOptions';
import NCROptionsForm from '../../components/small-format/NCROptionsForm';
import PadOptionsForm from '../../components/small-format/PadOptionsForm';
import LeafletOptionsForm from '../../components/small-format/LeafletOptionsForm';
import ContractJobSelector from '../../components/small-format/ContractJobSelector';
import { calculateFoldedDimensions } from '../../components/small-format/foldingUtils';
import { isFinishAvailable } from '../../components/small-format/finishingAvailability';
import { useSmallFormatFormLogic } from '../../hooks/useSmallFormatFormLogic';
import { toCamelCase } from '../../utils/caseTransformers';
const FOLD_ID = "fold";
const CREASE_ID = "crease";
const CUSTOM_JOB_TYPE_ID = 'custom/bespoke';
const CONTRACT_JOB_TYPE_ID = 'contract-wirebound-books';

const SmallFormatCalculator = ({
  pricingTier,
  selectedCustomer = null,
  onResetCustomer = null,
  isDialogMode = false,
  onSaveEmailQuote = null,
  dialogBridge = null,
  initialEmailQuote = null
}) => {
  const {
    quoteToLoad,
    targetCalculator,
    clearLoadedQuote,
    addDraft,
    updateDraft,
    editingDraftId,
    stopEditingDraft
  } = useQuotes()

  // Get quote context functions
  const { addProductToQuote, currentQuote } = useQuotes();
  const { currentUser, hasRole } = useAuth();

  const {
    jobTypes, paperStocks, finishingProcesses, printers, miscCosts,
    paperSizes, finishingMaterials, isLoading, error
  } = useSmallFormatData(selectedCustomer);

  // State for UI
  const [activeTab, setActiveTab] = useState('options');
  const [showQuoteReview, setShowQuoteReview] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({ orderNumber: '', quoteName: '' });
  const [productAddSuccess, setProductAddSuccess] = useState(false);
  const [showImpositionForPartner, setShowImpositionForPartner] = useState(false);
  // State for quote loading
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteLoadWarnings, setQuoteLoadWarnings] = useState([]);
  // Ref to track if the current jobType was set by loading a quote, to prevent overwriting quote values with suggestions.
  const jobTypeLoadedFromQuoteRef = React.useRef(null);
  const [fieldsLoadedFromQuote, setFieldsLoadedFromQuote] = useState(new Set()); // Keep this for loadQuoteIntoCalculator

  // Dialog mode state
  const [isEmailQuoteMode, setIsEmailQuoteMode] = useState(false);
  const [originalEmailQuote, setOriginalEmailQuote] = useState(null);

  // Contract job state
  const [contractJobPrice, setContractJobPrice] = useState(0);
  const [contractJobBreakdown, setContractJobBreakdown] = useState(null);
  const [contractJobSelections, setContractJobSelections] = useState([]);
  const [contractJobConfig, setContractJobConfig] = useState(null);

  // Use the new custom hook
  const {
    // State
    jobType, paperStock, width, height, quantity, selectedFinishes, selectedMiscCosts,
    standardSize, isDoubleSided, colours, notes, bleed, numberOfDesigns, jobName, finishingOptions,
    isBooklet, pageCount, coverStock, separateCover, coverColours, coverIsDoubleSided, bindingType,
    selectedCoverFinishes, coverFinishingOptions,
    foldType, isFolded, dimensionInputType,
    ncrType, sheetsPerSet, setsPerPad, paperSelections, hasWraparoundShield,
    padGlueEdge, hasNumbering, numberingStartAt, hasLooseShield, sheetPrinters, sheetSides,
    padJobSheetsPerPad, // Destructure new state for 'pads'
    calculatedPrice, breakdown, impositionDetails, bookletDetails, foldingDetails, paperUsage,
    isSettingFromQuoteInternal, // ADD THIS LINE
    calculationError, detailsError,

    // Setters
    setJobType, setPaperStock, setWidth, setHeight, setQuantity, setSelectedFinishes,
    setSelectedMiscCosts, setPrinter, setStandardSize, setIsDoubleSided, setColours,
    setNotes, setBleed, setNumberOfDesigns, setJobName, setFinishingOptions,
    setIsBooklet, setPageCount, setCoverStock, setSeparateCover, setCoverColours,
    setCoverIsDoubleSided, setBindingType, setSelectedCoverFinishes, setCoverFinishingOptions,
    setFoldType, setDimensionInputType,
    setNcrType, setSheetsPerSet, setSetsPerPad, setPaperSelections, setHasWraparoundShield,
    setPadGlueEdge, setHasNumbering, setNumberingStartAt, setHasLooseShield, setSheetPrinters, setSheetSides,
    setPadJobSheetsPerPad, // Destructure new setter for 'pads'
    // setCalculatedPrice, setBreakdown, setImpositionDetails, setBookletDetails, // These are mostly set by the hook itself
    setIsSettingFromQuoteInternal, // Use the new setter
    // setFoldingDetails, setPaperUsage, setCalculationError, setDetailsError,

    // Functions
    // calculatePrice, // Not directly called by component anymore
    // loadJobSuggestion, // Not directly called by component anymore
    handleBindingTypeChange, // NEW: Get the new handler
    resetForm: resetFormLogic, // Renamed to avoid conflict if component had its own reset
    createFullProductObject,
    toggleFinishingProcess,
    toggleCoverFinishingProcess,
    toggleMiscCost,
    handleStandardSizeChange,
    handleOptionChange,
    handleCheckboxChange,
    handleCoverOptionChange,
    handleCoverCheckboxChange,
    markFieldAsUserModified, // Get the user modification tracking function
  } = useSmallFormatFormLogic({
    apiBaseUrl: process.env.REACT_APP_API_URL, // Pass API URL
    pricingTier, // NEW: Pass pricing tier for manager override
    selectedCustomer, // NEW: Pass selected customer for custom pricing
    jobTypes, paperStocks, finishingProcesses, printers, miscCosts, paperSizes, finishingMaterials, // Pass data
    isLoadingQuote, jobTypeLoadedFromQuoteRef, fieldsLoadedFromQuote // Pass quote loading related refs/state
  });

  // Computed price: use contract job price if it's a contract job, otherwise use standard calculated price
  const displayPrice = jobType === CONTRACT_JOB_TYPE_ID ? contractJobPrice : calculatedPrice;
  const displayBreakdown = jobType === CONTRACT_JOB_TYPE_ID ? contractJobBreakdown : breakdown;
  const selectedPaper = paperStocks.find(stock => stock.id === paperStock);
  const availableFinishingProcesses = finishingProcesses.filter(process =>
    isFinishAvailable(process, selectedPaper, jobType)
  );

  // TEMPORARY DEBUG - Add this near your other useEffects
  useEffect(() => {
    console.log('QUANTITY CHANGED:', quantity, 'isLoadingQuote:', isLoadingQuote);
  }, [quantity, isLoadingQuote]);

  // Add this debug useEffect to track what's changing quantity
  useEffect(() => {
    console.log('STANDARD SIZE CHANGED:', standardSize, 'isLoadingQuote:', isLoadingQuote);
  }, [standardSize, isLoadingQuote]);

  const loadQuoteIntoCalculator = useCallback(async (quote) => {
    // Flag to control loading logs
    const DEBUG_LOADING = true; // Keep this true for debugging

    if (DEBUG_LOADING) console.log('=== STARTING loadQuoteIntoCalculator (RECORD & REPLAY) ===');
    setIsSettingFromQuoteInternal(true); // Signal that we are setting from quote
    setIsLoadingQuote(true);
    setQuoteLoadWarnings([]);

    try {
      if (DEBUG_LOADING) console.log('Loading quote:', quote);

      // 1. Resolve the Item
      const rawItem = quote.items?.[0] || quote;

      // 2. Prepare Effective Item (Record & Replay Strategy)
      // Merge:
      // A. calculatorPayload (The Recorder Recipe) - transformed to camelCase
      // B. rawItem (Top-level columns) - already mostly camelCase from serializer, but ensures ID availability etc.

      // We exclude 'breakdown' and 'pricing_breakdown' from conversion to preserve snake_case receipt
      const payloadSnake = rawItem.calculator_payload || rawItem.calculatorPayload || {};
      const payloadCamel = toCamelCase(payloadSnake, ['breakdown', 'pricing_breakdown', 'pricingBreakdown']);

      // We also convert rawItem to ensure mixed snake_case legacy props are handled
      const itemCamel = toCamelCase(rawItem, ['breakdown', 'pricing_breakdown', 'pricingBreakdown', 'calculator_payload', 'calculatorPayload']);

      // Merge with payloadCamel taking precedence for calc inputs, but itemCamel props (like ID) preserved if missing
      const item = { ...itemCamel, ...payloadCamel };

      // Ensure breakdown is preserved (look in various places)
      item.breakdown = rawItem.pricing_breakdown || rawItem.pricingBreakdown || rawItem.breakdown;

      if (DEBUG_LOADING) console.log('Effective Loaded Item (camelCase):', item);
      if (DEBUG_LOADING) console.log('Job name in quote item:', item.jobName);

      let jobTypeIdFromQuoteValue = null; // Declare here, initialized to null


      // Collect warnings but don't block loading
      const warnings = [];
      if (item.paperStock && !paperStocks.find(p => p.id === item.paperStock)) {
        warnings.push(`Paper stock "${item.paperStockName || item.paperStock}" is no longer available. Please select a new paper stock.`);
      }

      const printerToUse = item.printerId || item.printer;
      if (printerToUse && !printers.find(p => p.id === printerToUse)) {
        warnings.push(`Printer is no longer available. Please select a new printer.`);
      }

      // Check for invalid finishing processes - handle both field names
      const finishesToValidate = item.finishes || item.finishing;
      if (finishesToValidate && Array.isArray(finishesToValidate)) {
        const invalidFinishes = finishesToValidate.filter(finishId =>
          !finishingProcesses.find(p => p.id === finishId)
        );
        if (invalidFinishes.length > 0) {
          warnings.push(`Some finishing processes are no longer available: ${invalidFinishes.join(', ')}`);
        }
      }

      if (warnings.length > 0) {
        setQuoteLoadWarnings(warnings);
      }

      // APPROACH 1: Try setting everything at once using functional updates
      if (DEBUG_LOADING) console.log('=== SETTING ALL STATE IMMEDIATELY ===');

      // Core job settings - FIXED: Map display name to ID
      if (item.jobType) {
        if (DEBUG_LOADING) console.log('Setting job type from item:', item.jobType);

        // Find job type by name (item.jobType contains the display name)
        const jobTypeObjFromQuote = jobTypes.find(jt => jt.name === item.jobType); // Renamed for clarity
        jobTypeIdFromQuoteValue = jobTypeObjFromQuote ? jobTypeObjFromQuote.id : item.jobType; // Assign value here

        if (DEBUG_LOADING) console.log('Mapped job type name to ID:', item.jobType, '→', jobTypeIdFromQuoteValue);

        setJobType(prev => {
          if (DEBUG_LOADING) console.log('JobType functional update - prev:', prev, 'new:', jobTypeIdFromQuoteValue);
          return jobTypeIdFromQuoteValue;
        });
        jobTypeLoadedFromQuoteRef.current = jobTypeIdFromQuoteValue; // Mark this jobType as loaded from quote
      }

      if (typeof item.quantity === 'number' && item.quantity > 0) {
        if (DEBUG_LOADING) console.log('Setting quantity:', item.quantity);
        setQuantity(prev => {
          if (DEBUG_LOADING) console.log('Quantity functional update - prev:', prev, 'new:', item.quantity);
          return item.quantity;
        });
      }
      // Handle dimensions - for folded items with dimension_input_type='finished',
      // use finished_width_mm/finished_height_mm as the display dimensions
      const dimensionInputType = item.dimensionInputType || item.dimension_input_type;
      const useFinishedDimensions = dimensionInputType === 'finished' &&
        (item.finishedWidthMm || item.finished_width_mm) &&
        (item.finishedHeightMm || item.finished_height_mm);

      if (useFinishedDimensions) {
        const finishedWidth = item.finishedWidthMm || item.finished_width_mm;
        const finishedHeight = item.finishedHeightMm || item.finished_height_mm;
        if (DEBUG_LOADING) console.log('Using finished dimensions (folded item):', finishedWidth, 'x', finishedHeight);
        if (DEBUG_LOADING) console.log('Unfolded dimensions were:', item.width, 'x', item.height);

        setWidth(prev => {
          if (DEBUG_LOADING) console.log('Width functional update - prev:', prev, 'new:', finishedWidth);
          return finishedWidth;
        });
        setHeight(prev => {
          if (DEBUG_LOADING) console.log('Height functional update - prev:', prev, 'new:', finishedHeight);
          return finishedHeight;
        });
      } else {
        // Use standard width/height for non-folded items or unfinished dimension input
        if (typeof item.width === 'number' && item.width > 0) {
          if (DEBUG_LOADING) console.log('Setting width:', item.width);
          setWidth(prev => {
            if (DEBUG_LOADING) console.log('Width functional update - prev:', prev, 'new:', item.width);
            return item.width;
          });
        }

        if (typeof item.height === 'number' && item.height > 0) {
          if (DEBUG_LOADING) console.log('Setting height:', item.height);
          setHeight(prev => {
            if (DEBUG_LOADING) console.log('Height functional update - prev:', prev, 'new:', item.height);
            return item.height;
          });
        }
      }

      // Paper stock and printer - FIXED: Ensure both IDs are correct
      if (item.paperStock) {
        if (DEBUG_LOADING) console.log('Setting paper stock from item:', item.paperStock);

        // Check if this paper stock ID actually exists
        const paperStockObj = paperStocks.find(p => p.id === item.paperStock);
        if (paperStockObj) {
          if (DEBUG_LOADING) console.log('Found paper stock object:', paperStockObj.name, 'for ID:', item.paperStock);
          setPaperStock(prev => {
            if (DEBUG_LOADING) console.log('PaperStock functional update - prev:', prev, 'new:', item.paperStock);
            return item.paperStock;
          });
        } else {
          if (DEBUG_LOADING) console.log('Paper stock ID not found:', item.paperStock, 'Available count:', paperStocks.length);
        }
      }

      const printerToLoad = item.printerId || item.printer;
      if (printerToLoad) {
        if (DEBUG_LOADING) console.log('Setting printer from item:', printerToLoad);

        // Check if this printer ID actually exists
        const printerObj = printers.find(p => p.id === printerToLoad);
        if (printerObj) {
          if (DEBUG_LOADING) console.log('Found printer object:', printerObj.name, 'for ID:', printerToLoad);
          setPrinter(prev => {
            if (DEBUG_LOADING) console.log('Printer functional update - prev:', prev, 'new:', printerToLoad);
            return printerToLoad;
          });
        } else {
          if (DEBUG_LOADING) console.log('Printer ID not found:', printerToLoad, 'Available printers:', printers.map(p => ({ id: p.id, name: p.name })));
        }
      }

      // Standard size
      if (item.standardSize) {
        if (DEBUG_LOADING) console.log('Setting standard size:', item.standardSize);
        setStandardSize(prev => {
          if (DEBUG_LOADING) console.log('StandardSize functional update - prev:', prev, 'new:', item.standardSize);
          return item.standardSize;
        });
      }

      // Basic print settings
      if (typeof item.isDoubleSided === 'boolean') {
        if (DEBUG_LOADING) console.log('Setting double sided:', item.isDoubleSided);
        setIsDoubleSided(prev => {
          if (DEBUG_LOADING) console.log('IsDoubleSided functional update - prev:', prev, 'new:', item.isDoubleSided);
          return item.isDoubleSided;
        });
      }

      if (item.colours) {
        if (DEBUG_LOADING) console.log('Setting colours:', item.colours);
        setColours(prev => {
          if (DEBUG_LOADING) console.log('Colours functional update - prev:', prev, 'new:', item.colours);
          return item.colours;
        });
      }

      if (typeof item.bleed === 'number') {
        if (DEBUG_LOADING) console.log('Setting bleed:', item.bleed);
        setBleed(prev => {
          if (DEBUG_LOADING) console.log('Bleed functional update - prev:', prev, 'new:', item.bleed);
          return item.bleed;
        });
      }

      if (typeof item.numberOfDesigns === 'number' && item.numberOfDesigns > 0) {
        if (DEBUG_LOADING) console.log('Setting numberOfDesigns:', item.numberOfDesigns);
        setNumberOfDesigns(prev => {
          if (DEBUG_LOADING) console.log('NumberOfDesigns functional update - prev:', prev, 'new:', item.numberOfDesigns);
          return item.numberOfDesigns;
        });
      }

      // Handle job name - only set if it exists in the quote, don't generate fallback names
      const jobNameCandidate = [
        item.jobName,
        item.draftName,
        quote.draftName,
        quote.quoteName,
        quote.jobName,
        quote.name
      ].find(name => typeof name === 'string' && name.trim() !== '');

      if (jobNameCandidate) {
        if (DEBUG_LOADING) console.log('Setting job name from quote:', jobNameCandidate);
        setJobName(prev => {
          if (DEBUG_LOADING) console.log('JobName functional update - prev:', prev, 'new:', jobNameCandidate);
          return jobNameCandidate;
        });
      } else {
        if (DEBUG_LOADING) console.log('No job name found in quote, leaving existing job name unchanged');
        // Don't generate a fallback name - preserve any existing job name or leave empty
      }

      if (item.notes) {
        if (DEBUG_LOADING) console.log('Setting notes:', item.notes);
        setNotes(prev => {
          if (DEBUG_LOADING) console.log('Notes functional update - prev:', prev, 'new:', item.notes);
          return item.notes;
        });
      }

      // Check if there's pricing tier info in the breakdown
      if (item.breakdown && item.breakdown.pricing_tier) {
        if (DEBUG_LOADING) console.log('Original quote pricing tier from breakdown:', item.breakdown.pricing_tier);
      }

      // Finishing processes and options
      // Handle both 'finishes' (internal convention) and 'finishing' (API/payload convention)
      const finishesToLoad = item.finishes || item.finishing;
      if (Array.isArray(finishesToLoad)) {
        if (DEBUG_LOADING) console.log('Setting finishes:', finishesToLoad);
        setSelectedFinishes(prev => {
          if (DEBUG_LOADING) console.log('SelectedFinishes functional update - prev:', prev, 'new:', finishesToLoad);
          return finishesToLoad;
        });
      }

      // Handle both finishing_options (snake_case from quotes) and finishingOptions (camelCase from drafts)
      const finishingOptionsToLoad = item.finishing_options || item.finishingOptions;
      if (finishingOptionsToLoad && typeof finishingOptionsToLoad === 'object') {
        if (DEBUG_LOADING) console.log('Setting finishing options:', finishingOptionsToLoad);
        setFinishingOptions(prev => {
          if (DEBUG_LOADING) console.log('FinishingOptions functional update - prev:', prev, 'new:', finishingOptionsToLoad);
          return finishingOptionsToLoad;
        });
      }

      // Misc costs
      if (Array.isArray(item.miscCosts)) {
        if (DEBUG_LOADING) console.log('Setting misc costs:', item.miscCosts);
        setSelectedMiscCosts(prev => {
          if (DEBUG_LOADING) console.log('SelectedMiscCosts functional update - prev:', prev, 'new:', item.miscCosts);
          return item.miscCosts;
        });
      }

      // Booklet-specific settings
      if (typeof item.isBooklet === 'boolean') {
        if (DEBUG_LOADING) console.log('Setting is booklet:', item.isBooklet);
        setIsBooklet(prev => {
          if (DEBUG_LOADING) console.log('IsBooklet functional update - prev:', prev, 'new:', item.isBooklet);
          return item.isBooklet;
        });

        if (item.isBooklet) { // This condition should be true based on setIsBooklet log
          if (DEBUG_LOADING) console.log('[BookletLoad] item.isBooklet is true. Processing booklet fields.');
          if (typeof item.pageCount === 'number') {
            if (DEBUG_LOADING) console.log('Setting page count:', item.pageCount);
            setPageCount(prev => {
              if (DEBUG_LOADING) console.log('PageCount functional update - prev:', prev, 'new:', item.pageCount);
              return item.pageCount;
            });
          }

          // Determine bindingType, inferring if necessary
          let finalBindingTypeToSet = item.bindingType;
          if (DEBUG_LOADING) console.log('[BookletLoad] Initial item.bindingType:', item.bindingType, ' (Type:', typeof item.bindingType, ')');

          if (!finalBindingTypeToSet && jobTypeIdFromQuoteValue) { // Use jobTypeIdFromQuoteValue for inference
            if (DEBUG_LOADING) console.log('[BookletLoad] item.bindingType is falsy. Attempting inference with jobTypeIdFromQuoteValue:', jobTypeIdFromQuoteValue);
            if (jobTypeIdFromQuoteValue === 'wirebound-books') {
              finalBindingTypeToSet = 'wire_bound';
            } else if (jobTypeIdFromQuoteValue === 'perfect-bound-books') {
              finalBindingTypeToSet = 'perfect_bound';
            } else if (['booklet', 'brochure', 'saddle-stitch'].includes(jobTypeIdFromQuoteValue)) {
              // Default these job types to saddle-stitch if bindingType is missing
              finalBindingTypeToSet = 'saddle-stitch';
            }
            if (finalBindingTypeToSet && DEBUG_LOADING) { // Check finalBindingTypeToSet before logging
              console.log(`[BookletLoad] Inferred bindingType "${finalBindingTypeToSet}" from jobTypeIdFromQuoteValue "${jobTypeIdFromQuoteValue}" as item.bindingType was missing.`);
            }
          }

          if (finalBindingTypeToSet) {
            if (DEBUG_LOADING) console.log('[BookletLoad] Setting binding type to:', finalBindingTypeToSet);
            setBindingType(prev => {
              if (DEBUG_LOADING) console.log('BindingType functional update - prev:', prev, 'new:', finalBindingTypeToSet);
              return finalBindingTypeToSet;
            });
          } else {
            if (DEBUG_LOADING) console.log('[BookletLoad] No bindingType in quote item and could not infer from jobType. BindingType state will not be set from quote, relying on defaults or suggestions.');
          }

          // Determine separateCover value, inferring if necessary for item.separateCover
          let separateCoverValueToSet = item.separateCover;
          if (typeof separateCoverValueToSet !== 'boolean') {
            const hasCoverStock = !!item.coverStock;
            const hasCoverColours = !!item.coverColours;
            const hasSelectedCoverFinishes = Array.isArray(item.selectedCoverFinishes) && item.selectedCoverFinishes.length > 0;
            const hasLegacyCoverProcesses = Array.isArray(item.coverProcesses) && item.coverProcesses.length > 0;

            if (hasCoverStock || hasCoverColours || hasSelectedCoverFinishes || hasLegacyCoverProcesses) {
              if (DEBUG_LOADING) console.log('[BookletLoad] Inferring separateCover = true as cover details are present and separateCover field is missing/not boolean.');
              separateCoverValueToSet = true;
            } else {
              if (DEBUG_LOADING) console.log('[BookletLoad] separateCover field is missing/not boolean, and no other cover details found. Defaulting to false.');
              separateCoverValueToSet = false;
            }
          }

          if (DEBUG_LOADING) console.log('[BookletLoad] Determined separateCoverValueToSet:', separateCoverValueToSet, '. Calling setSeparateCover.');
          setSeparateCover(prev => {
            if (DEBUG_LOADING) console.log('SeparateCover functional update - prev:', prev, 'new:', separateCoverValueToSet);
            return separateCoverValueToSet;
          });

          // Load selectedCoverFinishes, preferring new field but falling back to legacy
          let coverFinishesToLoad = [];
          if (Array.isArray(item.selectedCoverFinishes)) {
            coverFinishesToLoad = item.selectedCoverFinishes;
          } else if (Array.isArray(item.coverProcesses)) { // Fallback for legacy quotes
            coverFinishesToLoad = item.coverProcesses;
            if (DEBUG_LOADING) console.log('[BookletLoad] Using legacy item.coverProcesses for selectedCoverFinishes:', coverFinishesToLoad);
          }
          // Set selectedCoverFinishes state if there's data or if it was explicitly an empty array in the quote
          if (coverFinishesToLoad.length > 0 || Array.isArray(item.selectedCoverFinishes) || Array.isArray(item.coverProcesses)) {
            // THIS WAS A BUG: setSeparateCover was called instead of setSelectedCoverFinishes
            setSelectedCoverFinishes(prev => {
              if (DEBUG_LOADING) console.log('SelectedCoverFinishes functional update - prev:', prev, 'new:', coverFinishesToLoad);
              return coverFinishesToLoad;
            });
          }

          if (separateCoverValueToSet) { // Use the determined value for further logic
            if (item.coverStock) {
              if (DEBUG_LOADING) console.log('Setting cover stock:', item.coverStock);
              setCoverStock(prev => {
                if (DEBUG_LOADING) console.log('CoverStock functional update - prev:', prev, 'new:', item.coverStock);
                return item.coverStock;
              });
            }

            if (item.coverColours) {
              if (DEBUG_LOADING) console.log('Setting cover colours:', item.coverColours);
              setCoverColours(prev => {
                if (DEBUG_LOADING) console.log('CoverColours functional update - prev:', prev, 'new:', item.coverColours);
                return item.coverColours;
              });
            }

            if (typeof item.coverIsDoubleSided === 'boolean') {
              if (DEBUG_LOADING) console.log('Setting cover double sided:', item.coverIsDoubleSided);
              setCoverIsDoubleSided(prev => {
                if (DEBUG_LOADING) console.log('CoverIsDoubleSided functional update - prev:', prev, 'new:', item.coverIsDoubleSided);
                return item.coverIsDoubleSided;
              });
            }

            // setSelectedCoverFinishes is handled above to include legacy fallback

            if (item.coverFinishingOptions && typeof item.coverFinishingOptions === 'object') {
              if (DEBUG_LOADING) console.log('Setting cover finishing options:', item.coverFinishingOptions);
              setCoverFinishingOptions(prev => {
                if (DEBUG_LOADING) console.log('CoverFinishingOptions functional update - prev:', prev, 'new:', item.coverFinishingOptions);
                return item.coverFinishingOptions;
              });
            } else {
              if (DEBUG_LOADING) console.log('Setting empty cover finishing options');
              setCoverFinishingOptions(prev => {
                if (DEBUG_LOADING) console.log('CoverFinishingOptions functional update - prev:', prev, 'new: {}');
                return {};
              });
            }
          } else {
            if (DEBUG_LOADING) console.log('[BookletLoad] separateCoverValueToSet is false. Not setting cover specific details (stock, colours, finishes).');
          }
          // The 'else' for typeof item.separateCover === 'boolean' is handled by the inference logic now.
        }
      }

      if (
        item.jobType?.toLowerCase() === 'pads' &&
        typeof item.padJobSheetsPerPad === 'number' &&
        item.padJobSheetsPerPad > 0
      ) {
        setPadJobSheetsPerPad(item.padJobSheetsPerPad);
      }

      // NCR-specific settings
      if (item.jobType?.toLowerCase() === 'ncr') {
        if (DEBUG_LOADING) console.log('=== NCR-specific settings ===');
        if (DEBUG_LOADING) console.log('Loading NCR job type:', item.jobType);

        if (item.ncrType) {
          if (DEBUG_LOADING) console.log('Setting NCR type:', item.ncrType);
          setNcrType(prev => {
            if (DEBUG_LOADING) console.log('NcrType functional update - prev:', prev, 'new:', item.ncrType);
            return item.ncrType;
          });
        }

        if (typeof item.sheetsPerSet === 'number') {
          if (DEBUG_LOADING) console.log('Setting sheets per set:', item.sheetsPerSet);
          setSheetsPerSet(prev => {
            if (DEBUG_LOADING) console.log('SheetsPerSet functional update - prev:', prev, 'new:', item.sheetsPerSet);
            if (DEBUG_LOADING) console.log('SheetsPerSet set to:', item.sheetsPerSet);
            return item.sheetsPerSet;
          });
        }

        if (typeof item.setsPerPad === 'number') {
          if (DEBUG_LOADING) console.log('Setting sets per pad:', item.setsPerPad);
          setSetsPerPad(prev => {
            if (DEBUG_LOADING) console.log('SetsPerPad functional update - prev:', prev, 'new:', item.setsPerPad);
            if (DEBUG_LOADING) console.log('SetsPerPad set to:', item.setsPerPad);
            return item.setsPerPad;
          });
        }

        if (Array.isArray(item.paperSelections)) {
          if (DEBUG_LOADING) console.log('Setting paper selections:', item.paperSelections);
          setPaperSelections(prev => {
            if (DEBUG_LOADING) console.log('PaperSelections functional update - prev:', prev, 'new:', item.paperSelections);
            if (DEBUG_LOADING) console.log('PaperSelections set to:', item.paperSelections);
            return item.paperSelections;
          });
        }

        if (Array.isArray(item.sheetPrinters)) {
          if (DEBUG_LOADING) console.log('Setting sheet printers:', item.sheetPrinters);
          setSheetPrinters(prev => {
            if (DEBUG_LOADING) console.log('SheetPrinters functional update - prev:', prev, 'new:', item.sheetPrinters);
            if (DEBUG_LOADING) console.log('SheetPrinters set to:', item.sheetPrinters);
            return item.sheetPrinters;
          });
        }

        if (Array.isArray(item.sheetSides)) {
          if (DEBUG_LOADING) console.log('Setting sheet sides:', item.sheetSides);
          setSheetSides(prev => {
            if (DEBUG_LOADING) console.log('SheetSides functional update - prev:', prev, 'new:', item.sheetSides);
            if (DEBUG_LOADING) console.log('SheetSides set to:', item.sheetSides);
            return item.sheetSides;
          });
        }

        if (typeof item.hasWraparoundShield === 'boolean') {
          if (DEBUG_LOADING) console.log('Setting wraparound shield:', item.hasWraparoundShield);
          setHasWraparoundShield(prev => {
            if (DEBUG_LOADING) console.log('HasWraparoundShield functional update - prev:', prev, 'new:', item.hasWraparoundShield);
            if (DEBUG_LOADING) console.log('HasWraparoundShield set to:', item.hasWraparoundShield);
            return item.hasWraparoundShield;
          });
        }

        if (typeof item.hasLooseShield === 'boolean') {
          if (DEBUG_LOADING) console.log('Setting loose shield:', item.hasLooseShield);
          setHasLooseShield(prev => {
            if (DEBUG_LOADING) console.log('HasLooseShield functional update - prev:', prev, 'new:', item.hasLooseShield);
            if (DEBUG_LOADING) console.log('HasLooseShield set to:', item.hasLooseShield);
            return item.hasLooseShield;
          });
        }

        if (item.padGlueEdge) {
          if (DEBUG_LOADING) console.log('Setting pad glue edge:', item.padGlueEdge);
          setPadGlueEdge(prev => {
            if (DEBUG_LOADING) console.log('PadGlueEdge functional update - prev:', prev, 'new:', item.padGlueEdge);
            if (DEBUG_LOADING) console.log('PadGlueEdge set to:', item.padGlueEdge);
            return item.padGlueEdge;
          });
        }

        if (typeof item.hasNumbering === 'boolean') {
          if (DEBUG_LOADING) console.log('Setting has numbering:', item.hasNumbering);
          setHasNumbering(prev => {
            if (DEBUG_LOADING) console.log('HasNumbering functional update - prev:', prev, 'new:', item.hasNumbering);
            if (DEBUG_LOADING) console.log('HasNumbering set to:', item.hasNumbering);
            return item.hasNumbering;
          });
        }

        if (typeof item.numberingStartAt === 'number') {
          if (DEBUG_LOADING) console.log('Setting numbering start at:', item.numberingStartAt);
          setNumberingStartAt(prev => {
            if (DEBUG_LOADING) console.log('NumberingStartAt functional update - prev:', prev, 'new:', item.numberingStartAt);
            if (DEBUG_LOADING) console.log('NumberingStartAt set to:', item.numberingStartAt);
            return item.numberingStartAt;
          });
        }
      }

      // Handle folding settings
      if (item.foldType) {
        if (DEBUG_LOADING) console.log('Setting fold type:', item.foldType);
        setFoldType(prev => {
          if (DEBUG_LOADING) console.log('FoldType functional update - prev:', prev, 'new:', item.foldType);
          return item.foldType;
        });
      }

      // Handle dimension input type for folded items
      if (item.dimension_input_type) {
        if (DEBUG_LOADING) console.log('Setting dimension input type:', item.dimension_input_type);
        setDimensionInputType(prev => {
          if (DEBUG_LOADING) console.log('DimensionInputType functional update - prev:', prev, 'new:', item.dimension_input_type);
          return item.dimension_input_type;
        });
      }

      if (DEBUG_LOADING) console.log('✅ All state setters called with functional updates');

      // Track which fields were loaded from the quote
      const loadedFields = new Set();
      if (item.jobType) loadedFields.add('jobType');
      if (typeof item.quantity === 'number') loadedFields.add('quantity');
      if (typeof item.width === 'number') loadedFields.add('width');
      if (typeof item.height === 'number') loadedFields.add('height');
      if (item.paperStock) loadedFields.add('paperStock');
      if (item.standardSize) loadedFields.add('standardSize');
      if (typeof item.isDoubleSided === 'boolean') loadedFields.add('isDoubleSided');
      if (item.dimension_input_type) loadedFields.add('dimensionInputType'); // Add protection
      if (item.colours) loadedFields.add('colours');
      if (typeof item.bleed === 'number') loadedFields.add('bleed');
      if (typeof item.numberOfDesigns === 'number' && item.numberOfDesigns > 0) loadedFields.add('numberOfDesigns');
      if (Array.isArray(item.finishes) || Array.isArray(item.finishing)) loadedFields.add('finishes');
      if (Array.isArray(item.miscCosts)) loadedFields.add('miscCosts');
      // Protection for booklet fields
      if (typeof item.isBooklet === 'boolean') loadedFields.add('isBooklet');
      if (typeof item.pageCount === 'number') loadedFields.add('pageCount');
      // Protect bindingType if it was in the quote OR if we inferred it from a specific jobType
      let bindingTypeWasSetOrInferred = !!item.bindingType;
      if (!bindingTypeWasSetOrInferred) {
        const jobTypeIdFromQuoteForProtection = jobTypes.find(jt => jt.name === item.jobType)?.id || item.jobType;
        if (['wirebound-books', 'perfect-bound-books', 'booklet', 'brochure', 'saddle-stitch'].includes(jobTypeIdFromQuoteForProtection)) {
          bindingTypeWasSetOrInferred = true; // We would have inferred it
        }
      }
      if (bindingTypeWasSetOrInferred) loadedFields.add('bindingType');
      if (typeof item.separateCover === 'boolean') loadedFields.add('separateCover');
      if (item.coverStock) loadedFields.add('coverStock');
      if (item.coverColours) loadedFields.add('coverColours');
      if (typeof item.coverIsDoubleSided === 'boolean') loadedFields.add('coverIsDoubleSided');
      if (Array.isArray(item.selectedCoverFinishes)) loadedFields.add('selectedCoverFinishes');
      if (item.coverFinishingOptions) loadedFields.add('coverFinishingOptions');

      // Add protection for NCR fields
      if (item.jobType?.toLowerCase() === 'ncr') {
        if (item.ncrType) loadedFields.add('ncrType');
        if (typeof item.sheetsPerSet === 'number') loadedFields.add('sheetsPerSet');
        if (typeof item.setsPerPad === 'number') loadedFields.add('setsPerPad');
        if (Array.isArray(item.paperSelections)) loadedFields.add('paperSelections');
        if (Array.isArray(item.sheetPrinters)) loadedFields.add('sheetPrinters');
        if (Array.isArray(item.sheetSides)) loadedFields.add('sheetSides');
        if (typeof item.hasWraparoundShield === 'boolean') loadedFields.add('hasWraparoundShield');
        if (typeof item.hasLooseShield === 'boolean') loadedFields.add('hasLooseShield');
        if (item.padGlueEdge) loadedFields.add('padGlueEdge');
        if (typeof item.hasNumbering === 'boolean') loadedFields.add('hasNumbering');
      }
      setFieldsLoadedFromQuote(loadedFields);
      if (DEBUG_LOADING) console.log('Fields loaded from quote:', Array.from(loadedFields));

      // The jobTypeLoadedFromQuoteRef will protect it.

      // After another short delay, clear the fieldsLoadedFromQuote set.
      // The main protection against overwriting the *entire* suggestion
      // is now jobTypeLoadedFromQuoteRef. fieldsLoadedFromQuote still
      // protects individual fields if loadJobSuggestion were called for other reasons.
      setTimeout(() => {
        if (DEBUG_LOADING) console.log('=== LOADING COMPLETE ===');
        setIsLoadingQuote(false);

        setIsSettingFromQuoteInternal(false); // Signal that we are done setting from quote
        // Clear the loaded fields after a delay to allow job suggestions again
        setTimeout(() => {
          setFieldsLoadedFromQuote(new Set());
          if (DEBUG_LOADING) console.log('Cleared fieldsLoadedFromQuote set. jobTypeLoadedFromQuoteRef still holds:', jobTypeLoadedFromQuoteRef.current);
        }, 100); // Shorter delay, as jobTypeLoadedFromQuoteRef is the primary gate.

      }, 200);

    } catch (error) {
      console.error('Error loading quote into calculator:', error);
      setQuoteLoadWarnings(['An error occurred while loading the quote. Some settings may not have been restored.']);
      setIsLoadingQuote(false);
    }
  }, [
    // Add all dependencies used by loadQuoteIntoCalculator
    // State setters from useSmallFormatFormLogic are stable and don't need to be listed
    // Props from useSmallFormatData
    jobTypes, paperStocks, printers, finishingProcesses,
    // Local state setters
    setIsLoadingQuote, setQuoteLoadWarnings, setFieldsLoadedFromQuote,
    // Refs (though refs usually don't trigger re-runs, it's good practice if their .current is read)
    jobTypeLoadedFromQuoteRef,
    // Destructured setters from the hook (stable, but good to be explicit if linter complains)
    setJobType, setQuantity, setWidth, setHeight, setPaperStock, setPrinter, setStandardSize,
    setIsDoubleSided, setColours, setBleed, setJobName, setNotes, setNumberOfDesigns, setSelectedFinishes,
    setFinishingOptions, setSelectedMiscCosts, setIsBooklet, setPageCount, setBindingType,
    setSeparateCover, setCoverStock, setCoverColours, setCoverIsDoubleSided, setSelectedCoverFinishes, setCoverFinishingOptions,
    setNcrType, setSheetsPerSet, setSetsPerPad, setPaperSelections, setHasWraparoundShield,
    setHasLooseShield, setPadGlueEdge, setPadJobSheetsPerPad, setHasNumbering, setNumberingStartAt, setSheetPrinters, setSheetSides,
    setFoldType, setDimensionInputType, setIsSettingFromQuoteInternal
  ]);

  // NEW: Quote Loading Effect - Add this after existing state but before other useEffects
  useEffect(() => {
    if (quoteToLoad && targetCalculator === 'small-format') {
      const dataLoaded = paperStocks.length > 0 && printers.length > 0 && finishingProcesses.length > 0 && jobTypes.length > 0;

      if (dataLoaded) {
        loadQuoteIntoCalculator(quoteToLoad);
        clearLoadedQuote(); // Clear after loading
      } else {
        // Data will load soon, this effect will re-run when arrays populate
      }
    }
  }, [quoteToLoad, targetCalculator, paperStocks, finishingProcesses, printers, jobTypes, clearLoadedQuote, loadQuoteIntoCalculator]);

  // Email quote loading for dialog mode
  useEffect(() => {
    // Any dialog session should use email-quote mode UX (even blank) so we always send back to Outlook
    if (isDialogMode && !isEmailQuoteMode) {
      setIsEmailQuoteMode(true);
    }

    if (initialEmailQuote && isDialogMode) {
      const dataLoaded = paperStocks.length > 0 && printers.length > 0 &&
        finishingProcesses.length > 0 && jobTypes.length > 0;

      if (dataLoaded) {
        console.log('Loading email quote in dialog mode:', initialEmailQuote);
        setOriginalEmailQuote(initialEmailQuote);
        loadQuoteIntoCalculator(initialEmailQuote);
      }
    }
  }, [
    initialEmailQuote,
    isDialogMode,
    isEmailQuoteMode,
    paperStocks,
    printers,
    finishingProcesses,
    jobTypes,
    loadQuoteIntoCalculator
  ]);

  // Determine if the main "Fold" or "Crease" finishing process is selected
  const showFoldingOptions = selectedFinishes.includes(FOLD_ID) ||
    selectedFinishes.includes(CREASE_ID);

  // Show LeafletOptionsForm if "Fold" or "Crease" is selected and it's not a booklet
  const showLeafletOptions = showFoldingOptions && !isBooklet;

  // Create contract job products - one separate quote item per selected variation
  const createContractJobProducts = () => {
    if (!contractJobBreakdown || !contractJobSelections.length) return [];

    const jobTypeName = jobTypes.find(jt => jt.id === jobType)?.name || 'Contract Wirebound Books';
    const technicalDesc = contractJobConfig?.technical_description || '';

    return contractJobSelections.map(selection => {
      const variation = contractJobBreakdown.variations.find(
        v => v.variation_id === selection.variation_id
      );

      if (!variation) return null;

      return {
        id: 'temp_' + Math.random().toString(36).substring(2, 15),
        productType: 'small_format',
        jobType: jobTypeName,
        jobName: `${jobTypeName} - ${variation.variation_name}`,
        quantity: selection.quantity,
        dimensions: `${variation.page_count} pages, ${variation.dimensions}`,
        material: variation.inner_details?.stock_name || variation.cover_details?.stock_name,
        materialNames: variation.inner_details?.stock_name || variation.cover_details?.stock_name,
        paperStockName: variation.inner_details?.stock_name,
        processes: ['Wirebound'],
        price: variation.total_price,
        breakdown: variation,
        isDoubleSided: true,
        colours: 'full_colour',
        addedAt: new Date().toISOString(),

        // Contract job specific data
        contractJobData: {
          variation_id: variation.variation_id,
          variation_name: variation.variation_name,
          page_count: variation.page_count,
          paper_stock: variation.inner_details?.stock_name,
          binding: 'Wirebound',
          technical_description: technicalDesc
        }
      };
    }).filter(Boolean);
  };

  // Get products to add - returns array of products
  const getProductsToAdd = () => {
    if (jobType === CONTRACT_JOB_TYPE_ID) {
      return createContractJobProducts();
    }
    return [createFullProductObject()]; // Wrap in array for consistency
  };

  const handleAddToQuote = () => {
    if (displayPrice <= 0) {
      alert('Please configure your print job before adding to quote.');
      return;
    }

    const products = getProductsToAdd();

    if (!products || products.length === 0 || products.some(p => !p)) {
      alert('There was an error creating the product. Please try again.');
      return;
    }

    // Add each product (contract jobs may have multiple)
    products.forEach(product => {
      addProductToQuote(product);
    });

    setProductAddSuccess(true);
    setTimeout(() => {
      setProductAddSuccess(false);
    }, 3000);
    resetFormLogic(); // Use the reset function from the hook
    // Preserve customer selection when adding to quote (don't call onResetCustomer)
  };
  // Open review quote modal
  const openQuoteReview = () => {
    // If there are no items in the quote AND no product is currently configured, then alert.
    if (currentQuote.items.length === 0 && (displayPrice <= 0 || calculationError)) {
      alert('Please configure at least one product before reviewing the quote.');
      return;
    }
    // If a product is configured (displayPrice > 0 and no error), add it to the quote.
    // This ensures that any product visible in the calculator's total is part of the review.
    if (displayPrice > 0 && !calculationError) {
      const products = getProductsToAdd();

      if (products && products.length > 0) {
        products.forEach(product => {
          if (product) addProductToQuote(product);
        });
        resetFormLogic(); // Reset the calculator form as the item is now considered "moved" to the quote.
        // Preserve customer selection when moving to quote review (don't call onResetCustomer)
      }
    }
    setShowQuoteReview(true);
  };

  // Reset form
  const handleResetForm = () => {
    resetFormLogic();

    // Clear selected customer when resetting
    if (onResetCustomer && typeof onResetCustomer === 'function') {
      onResetCustomer();
    }

    setActiveTab('options');
    window.scrollTo(0, 0);
  };

  // Action bar responsiveness: detect when space is tight
  const actionBarRowRef = React.useRef(null);
  const actionBarLeftRef = React.useRef(null);
  const actionBarRightRef = React.useRef(null);
  const [isActionBarTight, setIsActionBarTight] = useState(false);

  const recomputeActionBarTightness = useCallback(() => {
    const row = actionBarRowRef.current;
    const left = actionBarLeftRef.current;
    const right = actionBarRightRef.current;
    if (!row || !left || !right) return;
    const totalContentWidth = left.offsetWidth + right.scrollWidth;
    const available = row.clientWidth;
    setIsActionBarTight(totalContentWidth > available);
  }, []);

  useEffect(() => {
    recomputeActionBarTightness();
    const onResize = () => {
      // Use rAF to avoid layout thrash
      window.requestAnimationFrame(recomputeActionBarTightness);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recomputeActionBarTightness]);

  // Recompute when relevant UI state changes
  useEffect(() => {
    recomputeActionBarTightness();
  }, [editingDraftId, activeTab, displayPrice, currentQuote.items.length, recomputeActionBarTightness]);

  /**
   * IMPORTANT: This function is ONLY used for email quotes (Outlook add-in integration).
   * Regular drafts now use createFullProductObject() to maintain consistency with quotes.
   *
   * TODO: Refactor email quote handling to use quote format, then remove this function.
   * Currently retained to avoid breaking Outlook add-in integration.
   *
   * KNOWN LIMITATION: Missing NCR per-sheet fields (sheetPrinters, sheetSides).
   * This is acceptable because email quotes don't currently handle complex NCR configurations.
   */
  const buildDraftData = () => {
    // Get current form state using the hook's data
    const jobTypeName = jobTypes.find(jt => jt.id === jobType)?.name || '';
    const paperStockName = paperStocks.find(p => p.id === paperStock)?.name || '';
    const printerName = printers.find(p => p.id === (breakdown?.printer_id || 'colour'))?.name || '';

    // Get finishing process names
    const finishingNames = selectedFinishes.map(id => {
      const process = finishingProcesses.find(p => p.id === id);
      return process ? process.name : '';
    }).filter(Boolean);

    const resolvedJobName = (jobName && jobName.trim() !== '')
      ? jobName
      : `${jobTypeName} Draft ${new Date().toLocaleDateString()}`;

    const draftData = {
      // Calculator type
      calculatorType: 'small-format',

      // Form data
      jobType: jobTypeName,
      paperStock,
      paperStockName,
      width,
      height,
      quantity,
      standardSize,
      isDoubleSided,
      colours,
      bleed,
      numberOfDesigns,
      notes,
      finishes: selectedFinishes,
      finishingOptions,
      selectedMiscCosts,

      // Booklet data
      isBooklet,
      pageCount,
      bindingType,
      separateCover,
      coverStock,
      coverColours,
      coverIsDoubleSided,
      selectedCoverFinishes,
      coverFinishingOptions,

      // NCR data
      ncrType,
      sheetsPerSet,
      setsPerPad,
      paperSelections,
      hasWraparoundShield,
      padGlueEdge,
      hasNumbering,
      numberingStartAt,
      hasLooseShield,

      // Folding data
      foldType,
      dimensionInputType,

      // Pads data
      padJobSheetsPerPad,

      // Calculated data
      price: displayPrice,
      totalPrice: displayPrice,
      breakdown: displayBreakdown,

      // Display data
      dimensions: `${width}mm x ${height}mm`,
      jobName: resolvedJobName,
      draftName: resolvedJobName,

      // Metadata
      productType: 'small-format',
      printerId: breakdown?.printer_id || 'colour',
      printer: printerName,
      processes: finishingNames,
      createdAt: new Date().toISOString()
    };
    return draftData;
  };

  const handleSaveDraft = async () => {
    // Use quote format for drafts (includes all NCR per-sheet settings like sheetPrinters and sheetSides)
    const draftData = createFullProductObject();

    if (!draftData) {
      alert('Please configure your job before saving as draft.');
      return;
    }

    console.log('Saving small format draft:', draftData);

    const savedDraft = await addDraft(draftData);
    if (savedDraft) {
      alert(editingDraftId ? 'Saved as a new draft.' : 'Draft saved successfully!');
    } else {
      alert('Failed to save draft. Please try again.');
    }
  };

  const handleUpdateDraft = async () => {
    if (!editingDraftId) {
      await handleSaveDraft();
      return;
    }

    const draftData = createFullProductObject();

    if (!draftData) {
      alert('Please configure your job before updating the draft.');
      return;
    }

    const result = await updateDraft(editingDraftId, draftData);
    if (result?.success) {
      alert('Draft updated successfully!');
      stopEditingDraft();
      handleResetForm();
    } else {
      alert(`Failed to update draft. ${result?.message || ''}`);
    }
  };

  const handleExitEditing = () => {
    stopEditingDraft();
  };

  // Dialog mode: Return updated quote to Outlook add-in
  const handleSaveEmailQuote = async () => {
    // In dialog mode we always send back to Outlook via provided handler
    if (isDialogMode && onSaveEmailQuote) {
      try {
        const currentQuoteData = buildDraftData();
        await onSaveEmailQuote(currentQuoteData, originalEmailQuote);
        // Dialog will close via parent
        return;
      } catch (error) {
        console.error('Error saving email quote:', error);
        alert('Failed to save email quote. Please try again.');
        return;
      }
    }

    // Fallback for non-dialog usage
    await handleSaveDraft();
  };

  // NEW: Adjust label for size input based on dimensionInputType
  const sizeInputLabel = isBooklet
    ? 'Finished Size'
    : showLeafletOptions
      ? `Size (Input as ${dimensionInputType === 'finished' ? 'Finished' : 'Unfolded'})`
      : 'Size';

  // NEW: Adjust dimensions displayed in Job Summary
  const summaryWidth = isBooklet ? width : (isFolded && !isBooklet ?
    (dimensionInputType === 'finished' ? width : calculateFoldedDimensions(width, height, foldType).width) : width);
  const summaryHeight = isBooklet ? height : (isFolded && !isBooklet ?
    (dimensionInputType === 'finished' ? height : calculateFoldedDimensions(width, height, foldType).height) : height);
  const summaryDimensions = isBooklet
    ? `${summaryWidth}mm x ${summaryHeight}mm (${pageCount} pages)`
    : `${summaryWidth}mm x ${summaryHeight}mm`;

  const ImpositionContent = () => (
    <>
      {impositionDetails.usable_width > 0 && impositionDetails.usable_height > 0 ? (
        <ImpositionVisualizer
          impositionData={impositionDetails}
          foldType={showFoldingOptions ? foldType : null}
          foldOrientation={breakdown?.fold_orientation || "vertical"}
          isSaddleStitch={isBooklet && bindingType === 'saddle-stitch'}
          finalWidth={summaryWidth}
          finalHeight={summaryHeight}
          unfoldedWidth={isFolded ? breakdown?.unfolded_dimensions_raw?.width || 0 : 0}
          unfoldedHeight={isFolded ? breakdown?.unfolded_dimensions_raw?.height || 0 : 0}
          title={null}
          foldingDetailsData={foldingDetails}
        />
      ) : (
        <p className="text-sm text-center text-gray-500">Imposition preview not available (invalid dimensions).</p>
      )}
    </>
  );

  //console.log("--- Imposition Check ---");
  //console.log("Job Type:", jobType);
  //console.log("Is Booklet State:", isBooklet);
  //console.log("Binding Type State:", bindingType);
  //console.log("Condition (isBooklet && bindingType === 'saddle_stitch'):", isBooklet && bindingType === 'saddle_stitch');
  //console.log("Imposition Details State:", impositionDetails);
  //console.log("Show Folding Options:", showFoldingOptions);
  //console.log("Fold Type State:", foldType);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Email Quote Banner (Dialog Mode) */}
      {isEmailQuoteMode && isDialogMode && originalEmailQuote && (
        <div className="p-4 mb-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <h3 className="font-medium text-blue-900 mb-2">Editing Email Auto-Quote</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Original Price:</strong> £{(originalEmailQuote.originalPrice || originalEmailQuote.price || originalEmailQuote.totalPrice || 0).toFixed(2)}</p>
            {selectedCustomer?.customer_name && (
              <p><strong>Customer:</strong> {selectedCustomer.customer_name}</p>
            )}
            <p><strong>Pricing Tier:</strong> {(pricingTier || 'retail').charAt(0).toUpperCase() + (pricingTier || 'retail').slice(1)}</p>
            <p className="text-xs text-blue-600 mt-2">Adjust the configuration below and click "Save Updated Quote" when ready.</p>
          </div>
        </div>
      )}

      {/* Quote Loading Notification */}
      {isLoadingQuote && (
        <div className="p-4 mb-4 bg-blue-100 text-blue-700 rounded">
          Loading quote settings...
        </div>
      )}

      {editingDraftId && (
        <div className="p-4 mb-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-medium">Editing draft</h3>
              <p className="text-sm text-emerald-800">
                Use "Update Draft" to overwrite the original draft, or "Save as Draft" to create a new one.
              </p>
            </div>
            <button
              type="button"
              onClick={handleExitEditing}
              className="text-sm px-3 py-2 border border-emerald-300 text-emerald-800 rounded hover:bg-emerald-100"
            >
              Exit Editing
            </button>
          </div>
        </div>
      )}

      <CustomPricingBanner
        selectedCustomer={selectedCustomer}
        onDismiss={() => { }}
      />

      {/* Quote Loading Warnings */}
      {quoteLoadWarnings.length > 0 && (
        <div className="p-4 mb-4 bg-yellow-100 text-yellow-800 rounded">
          <h4 className="font-medium mb-2">Settings Updated</h4>
          <p className="text-sm mb-2">The quote has been loaded, but some items were updated:</p>
          <ul className="text-sm list-disc list-inside space-y-1">
            {quoteLoadWarnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
          <button
            onClick={() => setQuoteLoadWarnings([])}
            className="mt-2 text-xs text-yellow-600 underline hover:text-yellow-700"
          >
            Dismiss
          </button>
        </div>
      )}
      {calculationError && (
        <div className="p-4 mb-4 bg-red-100 text-red-700 rounded">
          {calculationError}
        </div>
      )}
      {detailsError && !calculationError && (
        <div className="p-4 mb-4 bg-yellow-100 text-yellow-800 rounded">
          Note: {detailsError} (Price calculation was successful)
        </div>
      )}
      {/* Display initial loading error from the hook */}
      {error && !calculationError && !detailsError && (
        <div className="p-4 mb-4 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}
      {isLoading ? (
        <div className="p-4 text-center">Loading pricing data...</div>
      ) : (
        <div className="bg-white px-6 pt-3 pb-6 rounded-lg shadow-md mb-24">
          <form className="pb-24">
            <div className="flex justify-center mb-6 border-b">
              <button
                type="button"
                className={`px-4 py-2 font-medium text-sm ${activeTab === 'options'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => setActiveTab('options')}
              >
                Job Options
              </button>
              <button
                type="button"
                className={`px-4 py-2 font-medium text-sm ${activeTab === 'preview'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : jobType === CONTRACT_JOB_TYPE_ID
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => setActiveTab('preview')}
                disabled={jobType === CONTRACT_JOB_TYPE_ID}
              >
                Preview & Breakdown
              </button>
            </div>

            {activeTab === 'options' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Job Type</label>
                    <select
                      value={jobType} // User manually changed jobType, clear the quote load marker
                      onChange={(e) => {
                        setJobType(e.target.value);
                        jobTypeLoadedFromQuoteRef.current = null;
                      }}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Select job type</option>
                      {jobTypes.map((jobType) => (
                        <option key={jobType.id} value={jobType.id}>
                          {jobType.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Quantity Input - hidden for contract jobs as quantity is set per variation */}
                  {jobType !== CONTRACT_JOB_TYPE_ID && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium mb-1">Quantity</label>
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                        className="w-full p-2 border rounded"
                        min="1"
                      />
                    </div>
                  )}
                </div>

                {/* Contract Job Selector - replaces standard form fields when active */}
                {jobType === CONTRACT_JOB_TYPE_ID ? (
                  <div className="md:col-span-2">
                    <ContractJobSelector
                      jobTypeId={CONTRACT_JOB_TYPE_ID}
                      onPriceCalculated={(price, breakdown, config) => {
                        setContractJobPrice(price);
                        setContractJobBreakdown(breakdown);
                        setContractJobConfig(config);
                      }}
                      onSelectionChange={(selections) => {
                        setContractJobSelections(selections);
                      }}
                    />
                  </div>
                ) : (
                  <>
                    {/* --- Size Selector --- */}
                    <div> {/* Keep size selector in one column */}
                      <label className="block text-sm font-medium mb-1">
                        {sizeInputLabel} {/* Use the new dynamic label here */}
                      </label>
                      <PaperSizeSelector
                        paperSizes={paperSizes}
                        selectedSize={standardSize}
                        onChange={handleStandardSizeChange}
                        width={width}
                        height={height}
                        onWidthChange={setWidth}
                        onHeightChange={setHeight}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Paper Stock</label>
                      {/* Disable standard paper selector for NCR jobs */}
                      <PaperSelector
                        paperStocks={paperStocks}
                        selectedPaperId={paperStock}
                        onChange={setPaperStock}
                        className="w-full"
                        disabled={jobType === 'ncr'}
                        currentJobType={jobType}
                      />
                      {jobType === 'ncr' && <p className="text-xs text-gray-500 mt-1">Paper for NCR jobs is selected within the NCR Options section below.</p>}
                    </div>
                  </>
                )}

                {/* --- Bleed setting (HIDDEN - preserved for backend processing) --- */}
                {/* 
              <div>
                <label className="block text-sm font-medium mb-1">
                  Bleed (mm)
                </label>
                <input
                  type="number"
                  value={bleed}
                  onChange={(e) => setBleed(Math.max(0, Number(e.target.value)))}
                  className="w-full p-2 border rounded"
                  min="0"
                  max="10"
                  step="0.5"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Standard bleed is 3mm. Set to 0 for no bleed.
                </p>
              </div>
              */}

                {/* --- Number of Designs field - hidden for contract jobs --- */}
                {jobType !== CONTRACT_JOB_TYPE_ID && (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Number of Different Designs
                    </label>
                    <input
                      type="number"
                      value={numberOfDesigns}
                      onChange={(e) => setNumberOfDesigns(Math.max(1, Number(e.target.value)))}
                      className="w-full p-2 border rounded"
                      min="1"
                      max="50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How many different designs are in this job? Additional designs may incur extra setup costs. The Quantity total should be the sum total of all designs. If not equal quantities, add a note in the text box below explaining how many of each design you require.
                    </p>
                  </div>
                )}

                {/* Long Sheet Indicator - keep below core inputs so it doesn't break the grid */}
                {jobType !== CONTRACT_JOB_TYPE_ID && (
                  <div className="md:col-span-2">
                    <LongSheetIndicator
                      width={width}
                      height={height}
                      isBooklet={isBooklet}
                      bindingType={bindingType}
                      bleed={bleed}
                    />
                  </div>
                )}

                {/* --- Conditional Booklet checkbox --- */}
                {jobType === CUSTOM_JOB_TYPE_ID && (
                  <div className="md:col-span-2 pl-4 border-l-4 border-blue-300">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isBookletCustom"
                        checked={isBooklet}
                        onChange={(e) => {
                          // Only allow changing state via checkbox if jobType is custom
                          if (jobType === CUSTOM_JOB_TYPE_ID) {
                            setIsBooklet(e.target.checked);
                          }
                        }}
                        className="mr-2"
                      />
                      <label htmlFor="isBookletCustom" className="font-medium">
                        Format this custom job as a booklet/brochure?
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Check this if your custom job requires booklet formatting (e.g., page imposition, binding).
                    </p>
                  </div>
                )}

                {isBooklet && (
                  <BookletOptionsForm
                    jobType={jobType}
                    bindingType={bindingType}
                    handleBindingTypeChange={handleBindingTypeChange} // Pass the new handler
                    pageCount={pageCount}
                    setPageCount={setPageCount}
                    separateCover={separateCover}
                    setSeparateCover={setSeparateCover}
                    coverStock={coverStock}
                    setCoverStock={setCoverStock}
                    paperStocks={paperStocks}
                    selectedFinishes={selectedFinishes}
                    finishingMaterials={finishingMaterials} // Ensure this is passed from useSmallFormatData
                    finishingOptions={finishingOptions}
                    handleOptionChange={handleOptionChange}
                    handleCheckboxChange={handleCheckboxChange}
                    coverColours={coverColours}
                    setCoverColours={setCoverColours}
                    coverIsDoubleSided={coverIsDoubleSided}
                    setCoverIsDoubleSided={setCoverIsDoubleSided}
                    selectedCoverFinishes={selectedCoverFinishes}
                    toggleCoverFinishingProcess={toggleCoverFinishingProcess}
                    coverFinishingOptions={coverFinishingOptions}
                    handleCoverOptionChange={handleCoverOptionChange}
                    handleCoverCheckboxChange={handleCoverCheckboxChange}
                    bookletDetails={bookletDetails}
                    finishingProcesses={finishingProcesses}
                    markFieldAsUserModified={markFieldAsUserModified}
                  />
                )}

                {/* --- Leaflet Options Form (Conditional) --- */}
                {showLeafletOptions && ( /* This component's root div has md:col-span-2 */
                  <LeafletOptionsForm
                    foldType={foldType}
                    setFoldType={setFoldType}
                    width={width}
                    height={height}
                    isDoubleSided={isDoubleSided}
                    foldingDetails={foldingDetails}
                    standardSize={standardSize}
                    dimensionInputType={dimensionInputType} // Pass state
                    setDimensionInputType={setDimensionInputType} // Pass setter
                  />
                )}

                {/* Add NCR Form */}
                {jobType === 'ncr' && ( // REMOVE !isSettingFromQuoteInternal from conditional rendering
                  <NCROptionsForm
                    ncrType={ncrType}
                    setNcrType={setNcrType}
                    setQuantity={setQuantity}
                    setsPerPad={setsPerPad}
                    setSetsPerPad={setSetsPerPad}
                    sheetsPerSet={sheetsPerSet}
                    setSheetsPerSet={setSheetsPerSet}
                    paperSelections={paperSelections}
                    setPaperSelections={setPaperSelections}
                    sheetPrinters={sheetPrinters}
                    setSheetPrinters={setSheetPrinters}
                    sheetSides={sheetSides}
                    setSheetSides={setSheetSides}
                    hasWraparoundShield={hasWraparoundShield}
                    setHasWraparoundShield={setHasWraparoundShield}
                    hasLooseShield={hasLooseShield}
                    setHasLooseShield={setHasLooseShield}
                    padGlueEdge={padGlueEdge}
                    setPadGlueEdge={setPadGlueEdge}
                    finishingOptions={finishingOptions}
                    setFinishingOptions={setFinishingOptions}
                    paperStocks={paperStocks}
                    printers={printers}
                    {...(() => console.log('SmallFormatCalculator: isSettingFromQuoteInternal value before passing to NCROptionsForm:', isSettingFromQuoteInternal))()}
                    selectedFinishes={selectedFinishes}
                    isSettingFromQuoteInternal={isSettingFromQuoteInternal}
                    setSelectedFinishes={setSelectedFinishes}
                    finishingProcesses={finishingProcesses}
                    hasNumbering={hasNumbering}
                    setHasNumbering={setHasNumbering}
                    numberingStartAt={numberingStartAt}
                    setNumberingStartAt={setNumberingStartAt}
                  />
                )}

                {/* Add PadOptionsForm for 'pads' job type */}
                {jobType === 'pads' && (
                  <PadOptionsForm
                    padJobSheetsPerPad={padJobSheetsPerPad}
                    setPadJobSheetsPerPad={setPadJobSheetsPerPad}
                    padGlueEdge={padGlueEdge} // Reusing from NCR/general state
                    setPadGlueEdge={setPadGlueEdge} // Reusing from NCR/general state
                  />
                )}

                {/* --- Sides and colour options (Spans Full Width) --- */}
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Sides */}
                  <div>
                    {jobType !== CONTRACT_JOB_TYPE_ID && (
                      <>
                        <label className="block text-sm font-medium mb-1">Sides</label>
                        <div className="flex">
                          <button
                            type="button"
                            className={`flex-1 py-2 px-4 rounded-l-lg ${!isDoubleSided
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-800'
                              }`}
                            onClick={() => {
                              setIsDoubleSided(false);
                              // If NCR job type, also update all sheets to single-sided
                              if (jobType === 'ncr') {
                                setSheetSides(Array(sheetsPerSet).fill(1));
                              }
                            }}
                          >
                            Single-sided
                          </button>
                          <button
                            type="button"
                            className={`flex-1 py-2 px-4 rounded-r-lg ${isDoubleSided
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-800'
                              }`}
                            onClick={() => {
                              setIsDoubleSided(true);
                              // If NCR job type, also update all sheets to double-sided
                              if (jobType === 'ncr') {
                                setSheetSides(Array(sheetsPerSet).fill(2));
                              }
                            }}
                          >
                            Double-sided
                          </button>
                        </div>
                        {jobType === 'ncr' && (
                          <p className="text-xs text-gray-500 mt-1">
                            NCR jobs are typically single-sided, but can be double-sided for special cases like terms and conditions on the back. Toggle this to update all sheets at once, or customize individual sheets above.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  {/* Colour */}
                  {jobType !== CONTRACT_JOB_TYPE_ID && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Colour</label>
                      <div className="flex">
                        <button
                          type="button"
                          className={`flex-1 py-2 px-4 rounded-l-lg ${colours === 'black_white'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-800'
                            }`}
                          onClick={() => {
                            setColours('black_white');
                            // If NCR job type, also update all sheets to black & white (mono)
                            if (jobType === 'ncr') {
                              setSheetPrinters(Array(sheetsPerSet).fill('mono'));
                            }
                          }}
                        >
                          Black & White
                        </button>
                        <button
                          type="button"
                          className={`flex-1 py-2 px-4 rounded-r-lg ${colours === 'full_colour'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-800'
                            }`}
                          onClick={() => {
                            setColours('full_colour');
                            // If NCR job type, also update all sheets to full colour
                            if (jobType === 'ncr') {
                              setSheetPrinters(Array(sheetsPerSet).fill('colour'));
                            }
                          }}
                        >
                          Full Colour
                        </button>
                      </div>
                      {jobType === 'ncr' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Toggle this to update all sheets at once, or customize individual sheets above.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* --- Finishing options (Spans Full Width) - hidden for contract jobs --- */}
                {jobType !== CONTRACT_JOB_TYPE_ID && (
                  <div className="md:col-span-2"> {/* Span 2 columns */}
                    <label className="block text-sm font-medium mb-2">
                      {isBooklet && separateCover ? "Inner Pages Finishing" : "Finishing Options"}
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {availableFinishingProcesses.map((process) => (
                        <div key={process.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`process-${process.id}`}
                            checked={selectedFinishes.includes(process.id)}
                            onChange={() => toggleFinishingProcess(process.id)}
                            className="mr-2"
                          />
                          <label htmlFor={`process-${process.id}`} className="text-sm">
                            {process.name}
                          </label>

                          {/* Use the new FinishingMaterialOptions component */}
                          {selectedFinishes.includes(process.id) &&
                            (['laminate', 'duplex-laminate', 'encapsulate', 'punch'].includes(process.id)) && (
                              <FinishingMaterialOptions
                                processId={process.id}
                                finishingOptions={finishingOptions}
                                handleOptionChange={handleOptionChange}
                                finishingMaterials={finishingMaterials}
                                className="ml-2"
                              />
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* --- Product Name (Hidden for contract jobs) --- */}
                {jobType !== CONTRACT_JOB_TYPE_ID && (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Product Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={jobName}
                      onChange={(e) => setJobName(e.target.value)}
                      className="w-full p-2 border rounded"
                      placeholder="Give this product a name (e.g. 'Company Brochure')"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      If left empty, a name will be generated based on the specifications.
                    </p>
                  </div>
                )}

                {/* --- Miscellaneous costs --- */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Additional Services</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {miscCosts.map((cost) => (
                      <div key={cost.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`cost-${cost.id}`}
                          checked={selectedMiscCosts.includes(cost.id)}
                          onChange={() => toggleMiscCost(cost.id)}
                          className="mr-2"
                        />
                        <label htmlFor={`cost-${cost.id}`} className="text-sm">
                          {cost.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* --- Notes --- */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full p-2 border rounded"
                    rows="3"
                    placeholder="Add any additional notes or requirements"
                  ></textarea>
                </div>

                <p className="md:col-span-2 mt-3 text-xs text-gray-500">
                  Prices are indicative and may change after artwork review.
                </p>

              </div>
            )}

            {activeTab === 'preview' && (
              <>
                <div className="mb-6 p-4 bg-gray-50 rounded border">
                  <h3 className="font-medium mb-3">Job Summary</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="col-span-2 md:col-span-1">
                      <strong>Job Type:</strong> {jobTypes.find(j => j.id === jobType)?.name || 'Not selected'}
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <strong>Size:</strong> {summaryDimensions} {standardSize ? `(${standardSize})` : ''} {/* Use dynamic summary dimensions */}
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <strong>Paper:</strong> {paperStocks.find(p => p.id === paperStock)?.name || 'Not selected'}
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <strong>Quantity:</strong> {quantity}
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <strong>Designs:</strong> {numberOfDesigns} {numberOfDesigns === 1 ? 'design' : 'designs'}
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <strong>Sides:</strong> {isDoubleSided ? 'Double-sided' : 'Single-sided'}
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <strong>Colour:</strong> {colours === 'full_colour' ? 'Full Colour' : 'Black & White'}
                    </div>
                    {isBooklet && (
                      <div className="col-span-2">
                        <strong>Booklet:</strong> {pageCount} pages
                      </div>
                    )}
                    {selectedFinishes.length > 0 && (
                      <div className="col-span-2">
                        <strong>Finishing:</strong> {selectedFinishes.map(id =>
                          finishingProcesses.find(p => p.id === id)?.name
                        ).filter(Boolean).join(', ')}
                      </div>
                    )}
                    {foldType && (
                      <div className="col-span-2">
                        <strong>Folding:</strong> {foldType.charAt(0).toUpperCase() + foldType.slice(1)} Fold
                      </div>
                    )}
                    {selectedFinishes.includes('wirebind') && finishingOptions?.wirebind?.binding_edge && (
                      <div className="col-span-2">
                        <strong>Wire Binding Edge:</strong> {finishingOptions.wirebind.binding_edge.charAt(0).toUpperCase() + finishingOptions.wirebind.binding_edge.slice(1)}
                      </div>
                    )}
                    {jobType === 'ncr' && (
                      <>
                        <div className="col-span-2">
                          <strong>NCR Type:</strong> {ncrType === 'sets' ? 'Individual Sets' : 'Padded Sets'}
                        </div>
                        <div className="col-span-2">
                          <strong>NCR Structure:</strong> {sheetsPerSet}-part form
                          {ncrType === 'pads' && `, ${setsPerPad} sets per pad`}
                        </div>
                        {paperSelections.length > 0 && (
                          <div className="col-span-2">
                            <strong>Paper Colors:</strong> {paperSelections.map(id =>
                              paperStocks.find(p => p.id === id)?.name || ''
                            ).filter(Boolean).join(', ')}
                          </div>
                        )}
                        {ncrType === 'pads' && (
                          <div className="col-span-2">
                            <strong>Pad Options:</strong>
                            {[
                              `Glued on ${padGlueEdge} edge`,
                            ].filter(Boolean).join(', ')}
                          </div>
                        )}
                        {hasNumbering && (
                          <div className="col-span-2">
                            <strong>Numbering:</strong> Starting at {numberingStartAt}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Paper usage */}
                <PaperUsagePreview
                  paperUsage={paperUsage}
                  isBooklet={isBooklet}
                  separateCover={separateCover}
                />

                {/* Imposition preview section */}
                {impositionDetails && (
                  <div className="mb-6">
                    {(currentUser?.pricing_tier === 'partner' || currentUser?.pricing_tier === 'choosyg') ? (
                      // Accordion for discount tier
                      <div className="border rounded">
                        <button
                          type="button"
                          onClick={() => setShowImpositionForPartner(!showImpositionForPartner)}
                          className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100"
                        >
                          <h3 className="font-medium">Imposition Preview</h3>
                          <svg
                            className={`w-5 h-5 transform transition-transform ${showImpositionForPartner ? 'rotate-180' : ''
                              }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {showImpositionForPartner && (
                          <div className="p-4 border-t"><ImpositionContent /></div>
                        )}
                      </div>
                    ) : (
                      // Always show for other tiers
                      <div>
                        <h3 className="font-medium mb-3">Imposition Preview</h3>
                        <div className="p-4 bg-gray-50 rounded border"><ImpositionContent /></div>
                      </div>
                    )}
                  </div>
                )}

                {/* Price breakdown */}
                {currentUser?.pricing_tier !== 'partner' && currentUser?.pricing_tier !== 'choosyg' && (
                  <PriceBreakdownTable
                    breakdown={breakdown}
                    showDetailedView={currentUser?.show_detailed_breakdown || false}
                    pricingTier={currentUser?.pricing_tier || 'retail'}
                  />
                )}
              </>
            )}
            {/* Success message (moved inside form, before fixed bar) */}
            {productAddSuccess && (
              <div className="mb-6 p-3 bg-green-100 text-green-800 rounded flex items-center justify-between">
                <span>Product added to quote!</span>
                <span className="text-sm text-green-600">
                  {currentQuote.items.length} item{currentQuote.items.length !== 1 ? 's' : ''} in quote
                </span>
              </div>
            )}
          </form>
        </div>
      )}
      {/* --- Fixed Action Bar --- */}
      {!isLoading && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-2 border-t border-gray-200 shadow-md z-20">
          {isDialogMode && isEmailQuoteMode ? (
            /* Dialog Mode Action Bar */
            <div className="max-w-4xl mx-auto flex flex-row justify-between items-center gap-2 sm:gap-4">
              <div className="font-bold text-lg sm:text-xl md:text-2xl truncate">
                Updated Price: £{displayPrice.toFixed(2)}
                {originalEmailQuote?.totalPrice && displayPrice !== originalEmailQuote.totalPrice && (
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    (was £{originalEmailQuote.totalPrice.toFixed(2)})
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => dialogBridge?.closeDialog({ action: 'cancel' })}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEmailQuote}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
                  disabled={displayPrice <= 0}
                >
                  Save Updated Quote
                </button>
              </div>
            </div>
          ) : (
            /* Normal Mode Action Bar */
            <div ref={actionBarRowRef} className="max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-4">
              {/* Left side: Price Display and Preview Buttons */}
              <div ref={actionBarLeftRef} className="flex items-center gap-3 sm:gap-4 min-w-0">
                {((pricingTier === 'partner' || pricingTier === 'choosyg') || (pricingTier === undefined && (currentUser?.pricing_tier === 'partner' || currentUser?.pricing_tier === 'choosyg'))) ? (
                  <div className="font-bold text-lg sm:text-xl md:text-2xl truncate">
                    Price: £{displayPrice.toFixed(2)}
                    {displayBreakdown?.partner_tier_details?.original_retail_price && (
                      <span className="hidden lg:inline text-xs lg:text-sm font-normal text-gray-600 ml-2">(Retail: £{displayBreakdown.partner_tier_details.original_retail_price.toFixed(2)})</span>
                    )}
                  </div>
                ) : (
                  <div className="font-bold text-lg sm:text-xl md:text-2xl truncate">
                    Total: £{displayPrice.toFixed(2)}
                  </div>
                )}

                {/* Preview Buttons (always present) */}
                {!isActionBarTight && activeTab === 'options' && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('preview')}
                    className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-gray-100 text-gray-700 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    title="Switch to Preview & Breakdown tab"
                  >
                    Preview
                  </button>
                )}
                {!isActionBarTight && activeTab === 'preview' && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('options')}
                    className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-gray-100 text-gray-700 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    title="Return to Job Options tab"
                  >
                    Options
                  </button>
                )}
              </div>

              {/* Right side: Action Buttons */}
              <div ref={actionBarRightRef} className="flex flex-nowrap whitespace-nowrap overflow-x-auto no-scrollbar gap-1.5 sm:gap-2 justify-start ml-auto">
                <button
                  type="button"
                  onClick={handleResetForm}
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-gray-200 text-gray-800 rounded"
                >
                  Reset
                </button>
                {/* NEW: Save as Draft Button */}
                {editingDraftId && (
                  <button
                    type="button"
                    onClick={handleUpdateDraft}
                    className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-green-600 text-white rounded disabled:bg-green-400"
                    disabled={
                      !jobType ||
                      (jobType === 'ncr' && (!paperSelections || paperSelections.length === 0 || paperSelections.some(s => !s) || paperSelections.length < sheetsPerSet)) ||
                      (jobType !== 'ncr' && !paperStock)
                    }
                  >
                    Update Draft
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-emerald-600 text-white rounded disabled:bg-emerald-400"
                  disabled={
                    !jobType || // Always disable if no job type is selected
                    (jobType === 'ncr' && (!paperSelections || paperSelections.length === 0 || paperSelections.some(s => !s) || paperSelections.length < sheetsPerSet)) || // For NCR, check paper selections and ensure all parts are selected
                    (jobType !== 'ncr' && !paperStock) // For non-NCR, check paperStock
                  }
                >
                  {editingDraftId ? 'Save as Draft' : 'Save Draft'}
                </button>
                {editingDraftId && (
                  <button
                    type="button"
                    onClick={handleExitEditing}
                    className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-gray-200 text-gray-800 rounded"
                  >
                    {isActionBarTight ? 'Exit' : 'Exit Editing'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleAddToQuote}
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-blue-600 text-white rounded disabled:bg-blue-400"
                  disabled={calculatedPrice <= 0}
                >
                  Add to Quote
                </button>
                <button
                  type="button"
                  onClick={openQuoteReview}
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-indigo-600 text-white rounded disabled:bg-indigo-400 relative mr-3"
                  disabled={currentQuote.items.length === 0 && calculatedPrice <= 0}
                >
                  <span>Review Quote</span>
                  {currentQuote.items.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-5 h-5 px-1 flex items-center justify-center">
                      {currentQuote.items.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quote Review Modal */}
      {showQuoteReview && (
        currentUser && (hasRole('manager') || hasRole('admin')) ? (
          <ManagerQuoteReview
            onClose={(result) => {
              setShowQuoteReview(false);

              // Handle Printlogic success with order number
              if (result && typeof result === 'object' && result.type === 'order_submitted_to_printlogic') {
                setSuccessModalData({
                  orderNumber: result.orderNumber,
                  quoteName: result.quoteName
                });
                setShowSuccessModal(true);
                handleResetForm();
              }
              // Handle other success cases
              else if (result && result !== 'add_more_items') {
                handleResetForm();
              }
            }}
            preSelectedCustomer={selectedCustomer}
          />
        ) : (
          <QuoteReview
            onClose={(result) => {
              setShowQuoteReview(false);

              // Handle Printlogic success with order number
              if (result && typeof result === 'object' && result.type === 'order_submitted_to_printlogic') {
                setSuccessModalData({
                  orderNumber: result.orderNumber,
                  quoteName: result.quoteName
                });
                setShowSuccessModal(true);
                handleResetForm();
              }
              // Handle other success cases
              else if (result && result !== 'add_more_items') {
                handleResetForm();
              }
            }}
            currentProduct={calculatedPrice > 0 && !calculationError && jobType !== CONTRACT_JOB_TYPE_ID ? createFullProductObject() : null}
          />
        )
      )}

      {/* Printlogic Success Modal */}
      <PrintlogicSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        orderNumber={successModalData.orderNumber}
        quoteName={successModalData.quoteName}
      />
    </div>
  );
};

// Long Sheet Indicator Component
const LongSheetIndicator = ({ width, height, isBooklet, bindingType, bleed }) => {
  if (!width || !height) return null;

  // Determine imposition dimensions
  let impositionWidth = width;
  let impositionHeight = height;

  if (isBooklet && bindingType === 'saddle-stitch') {
    impositionWidth = width * 2;  // Spread
    impositionHeight = height;
  }

  const margin = 0;  // No margin - bleed accounts for space
  const itemWithBleed_W = impositionWidth + (bleed * 2);
  const itemWithBleed_H = impositionHeight + (bleed * 2);

  // Check SRA3
  const usableSRA3_W = 320 - (margin * 2);
  const usableSRA3_H = 450 - (margin * 2);

  const fitsSRA3 = (
    (itemWithBleed_W <= usableSRA3_W && itemWithBleed_H <= usableSRA3_H) ||
    (itemWithBleed_W <= usableSRA3_H && itemWithBleed_H <= usableSRA3_W)
  );

  if (fitsSRA3) return null;  // No warning needed

  // Check long sheet
  const usableLong_W = 320 - (margin * 2);
  const usableLong_H = 640 - (margin * 2);

  const fitsLongSheet = (
    (itemWithBleed_W <= usableLong_W && itemWithBleed_H <= usableLong_H) ||
    (itemWithBleed_W <= usableLong_H && itemWithBleed_H <= usableLong_W)
  );

  if (!fitsLongSheet) {
    // Too large - error state
    const itemType = (isBooklet && bindingType === 'saddle-stitch') ? 'Spread' : 'Item';
    return (
      <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="font-medium text-red-800">Size Too Large</p>
            <p className="text-sm text-red-700">
              {itemType} size ({impositionWidth}×{impositionHeight}mm) exceeds maximum printable size (320×640mm)
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Fits on long sheet - warning state
  const itemType = (isBooklet && bindingType === 'saddle-stitch') ? 'spread' : 'item';

  return (
    <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded">
      <div className="flex items-center">
        <svg className="w-5 h-5 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <div className="flex-1">
          <p className="font-medium text-amber-800">Long-Feed Printing Required</p>
          <p className="text-sm text-amber-700">
            Uses SRA2 sheet, trimmed for long-feed printing.
            {' '}{itemType.charAt(0).toUpperCase() + itemType.slice(1)} size: {impositionWidth}×{impositionHeight}mm
          </p>
        </div>
      </div>
    </div>
  );
};

export default SmallFormatCalculator;
